import { useCallback, useEffect, useRef, useState } from 'react';
import { hideFreshChat, initFreshChat, loadFreshchatScript, openFreshchat } from 'App/components/freshChat/FreshChat';
import { useIsLoggedIn } from 'App/services/idpTokenService';
import useFreshChatEnabled from 'Shared/hooks/useFreshChatEnabled';
import { useUserPointsFromACT } from 'Shared/services/act/app/actService';
import { useGetUserStats } from 'Shared/services/red/api/redAPI';
import { useGetUserInfo } from 'Shared/services/userActivation/api/idpApiService';
import { useUserVerificationStatus } from 'Shared/services/ver/app/verificationService';

interface UseFreshChatReturn {
  openChat          : () => Promise<void>;
  isFreshChatEnabled: boolean;
  isLoading         : boolean;
  isLauncherVisible : boolean;
}

/**
 * Get the current date and the beginning of the year
 */
const currentDateTime    = new Date();
const beginningOfTheYear = new Date(currentDateTime.getFullYear(), 0, 1);

/**
 * A React hook that manages FreshChat lazy loading and opening
 * @returns {UseFreshChatReturn}
 */
const useFreshChat = (): UseFreshChatReturn => {
  const isLoggedIn                                = useIsLoggedIn();
  const { data: pointsData }                      = useUserPointsFromACT(true);
  const { data: userInfo }                        = useGetUserInfo(true);
  const { data: verificationStatusData }          = useUserVerificationStatus(true);
  const { enabled: isFreshChatEnabled }           = useFreshChatEnabled();
  const { data: userStats }                       = useGetUserStats(beginningOfTheYear.toISOString(), currentDateTime.toISOString(), isLoggedIn);
  const earnedPoints                              = userStats?.pointsEarned.toString() ?? '';
  const [isLoading, setIsLoading]                 = useState(false);
  const [isLauncherVisible, setIsLauncherVisible] = useState<boolean>(() => {
    return !!(window.fcWidget && window.fcWidget.isInitialized());
  });

  const isOpeningRef = useRef(false);

  useEffect(() => {
    if (window.fcWidget && window.fcWidget.isInitialized()) {
      setIsLauncherVisible(true);
    }

    if (!isFreshChatEnabled) {
      hideFreshChat();
    }
  }, [isFreshChatEnabled]);

  const openChat = useCallback(async () => {
    if (!isFreshChatEnabled || !isLoggedIn || isLoading || isOpeningRef.current) {
      return;
    }

    setIsLoading(true);
    isOpeningRef.current = true;

    try {
      await loadFreshchatScript();

      /**
       * Callback to handle launcher visibility
       */
      const handleLauncherVisible = () => {
        setIsLauncherVisible(true);
        setIsLoading(false);
        isOpeningRef.current = false;
      };

      /**
       * Initialize FreshChat
       * pass hideDefaultLauncher: true to ensure the first load doesn't show the bubble until opened.
       */
      initFreshChat(pointsData, userInfo, verificationStatusData, earnedPoints, {
        hideDefaultLauncher: true,
        onLauncherVisible: handleLauncherVisible
      });

      openFreshchat();

      /**
       * Ensure we stop loading after a timeout to prevent stuck UI.
       */
      setTimeout(() => {
        if (isOpeningRef.current) {
          setIsLoading(false);
          isOpeningRef.current = false;
        }
      }, 5000);

    } catch (error) {
      setIsLoading(false);
      isOpeningRef.current = false;
    }
  }, [isFreshChatEnabled, isLoggedIn, pointsData, userInfo, verificationStatusData, earnedPoints, isLoading]);

  return {
    openChat,
    isFreshChatEnabled,
    isLoading,
    isLauncherVisible
  };
};

export default useFreshChat;
